import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Footer from "./common/Footer";
import Header from "./common/Header";
import Preloader from '../components/common/Preloader';
import SlidersHolidays from "./sliders/holidays/SlidersHolidays";
import SwipperHolidays from './sliders/holidays/SwipperHolidays';
import FrontendPageSEO from '../components/common/FrontendPageSEO';
import SliderHolidaysExplore from "./sliders/holidays/SliderHolidaysExplore";
import Sliders1_Holidays from "./sliders/holidays/Sliders1_Holidays";
import LoginPopup from './popup/LoginPopup';
import axios from 'axios';
import useFrontendPageContent from '../components/common/useFrontendPageContent';
import DynamicSectionTitle from '../components/common/DynamicSectionTitle';
import GenericDynamicSection from '../components/common/GenericDynamicSection';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const styles = `
.search-container { position: relative; width: 100%; }
.suggestions-dropdown { position: absolute; top: 100%; left: 0; right: 0; background-color: white; border: 1px solid #ddd; border-top: none; border-radius: 0 0 8px 8px; z-index: 9999 !important; max-height: 300px; overflow-y: auto; text-align: left; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
.suggestion-item { padding: 12px 20px; cursor: pointer; border-bottom: 1px solid #eee; }
.suggestion-item:last-child { border-bottom: none; }
.suggestion-item:hover { background-color: #f5f5f5; }
.suggestion-item strong { color: #333; }
.suggestion-item small { color: #888; margin-left: 8px; text-transform: capitalize; }
`;

function Holidays() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoginPopupOpen, setIsLoginPopupOpen] = useState(false);
  const { pageContent, getSection, isSectionActive, sections, loading } = useFrontendPageContent('holidays');

  // Section keys that have dedicated specialized components on this page.
  // Any section added from the admin with a key NOT in this set will be
  // rendered automatically via GenericDynamicSection below.
  const KNOWN_SECTION_KEYS = new Set([
    'trending_destinations',
    'seasonal_deals',
    'holiday_themes',
    'international_destinations',
    'hidden_gems',
    'holiday_explore_gems',
  ]);

  // SEARCH STATE (mirrors Hotels page behavior but searches holiday packages by city & package_name)
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);

  useEffect(() => {
    // inject styles for suggestions dropdown (same approach as Hotels)
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);

    const themeStylesheet = document.getElementById("holidaypopup-css");
    if (location.pathname === "/holidays") {
      if (!themeStylesheet) {
        const link = document.createElement("link");
        link.id = "holidaypopup-css";
        link.rel = "stylesheet";
        link.href = "assets/css/LoginPopup.css";
        document.head.appendChild(link);
      }
    } else {
      if (themeStylesheet) {
        document.head.removeChild(themeStylesheet);
      }
    }

    return () => {
      if (styleSheet.parentNode) {
        document.head.removeChild(styleSheet);
      }
    };
  }, [location]);

  useEffect(() => {
    // only trigger when user typed at least 2 chars
    if (searchQuery.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    const debounceTimer = setTimeout(() => {
      const fetchSuggestions = async () => {
        setIsSuggestionsLoading(true);
        try {
          // endpoint expected to return packages matching package_name OR city
          const response = await axios.get(`${API_BASE_URL}/holiday-packages/search-packages?q=${encodeURIComponent(searchQuery)}`);
          // response.data.packages -> array
          const packages = Array.isArray(response.data?.packages) ? response.data.packages : [];

          // build city suggestions from returned packages (unique cities)
          const citySet = new Set();
          const citySuggestions = [];
          for (const p of packages) {
            const city = (p.city || '').trim();
            if (city && !citySet.has(city.toLowerCase())) {
              citySet.add(city.toLowerCase());
              citySuggestions.push({ type: 'city', id: null, name: city });
            }
          }

          // package suggestions
          const packageSuggestions = packages.map(p => ({
            type: 'package',
            id: p.id,
            name: p.package_name,
            city: p.city,
            price: p.package_price,
          }));

          // final suggestions: cities first then packages (similar UX to hotels)
          setSuggestions([...citySuggestions, ...packageSuggestions]);

        } catch (error) {
          console.error("Failed to fetch package suggestions:", error);
          setSuggestions([]);
        } finally {
          setIsSuggestionsLoading(false);
        }
      };
      fetchSuggestions();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/holidays-list?city=${encodeURIComponent(searchQuery.trim())}`);
      setSuggestions([]);
    }
  };

  const generateHolidaySlug = (pkg) => {
    const slugify = (str) => str ? str.toString().toLowerCase().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '').replace(/\-\-+/g, '-').replace(/^-+/, '').replace(/-+$/, '') : '';
    const parts = [
      slugify(pkg.package_name || pkg.name),
      slugify(pkg.city),
      Math.round(pkg.offer_price || pkg.package_price || pkg.price || 0),
      pkg.id
    ].filter(Boolean).join('-');
    return parts.replace(/--+/g, '-');
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.name || '');
    setSuggestions([]);
    if (suggestion.type === 'city') {
      navigate(`/holidays-list?city=${encodeURIComponent(suggestion.name)}`);
    } else if (suggestion.type === 'package') {
      navigate(`/holiday-details/${generateHolidaySlug(suggestion)}`);
    }
  };

  // --- rest of your original Holidays page UI kept intact below ---
  // (I replaced only the search form area with the above search logic)
  function updateClassBasedOnScreenSize() {
    const element = document.querySelector(".holidays-page-space");

    if (!element) return;

    if (window.innerWidth >= 1024) {
      element.classList.add("pb-5");
    } else {
      element.classList.remove("pb-5");
    }
  }

  window.addEventListener("load", updateClassBasedOnScreenSize);
  window.addEventListener("resize", updateClassBasedOnScreenSize);

  // keep placeholder lists and sliders as in your original file
  const trendingSection = getSection('trending_destinations', {
    title: 'Trending Destinations',
    title_highlight: 'Top',
    subtitle: 'Explore the hottest travel spots around the globe.',
    Items: [
    { id: 1, title: "Europe", image_url: "assets/img/tours/europe.jpg" },
    { id: 2, title: "Bali", image_url: "assets/img/tours/bali.jpg" },
    { id: 3, title: "Dubai", image_url: "assets/img/tours/dubai.jpg" },
    { id: 4, title: "Andaman", image_url: "assets/img/tours/andaman.jpg" },
    { id: 5, title: "Australia", image_url: "assets/img/tours/australia.jpg" },
    { id: 6, title: "Singapore", image_url: "assets/img/tours/singapore.jpg" },
    ],
  });
  const seasonalDeals = getSection('seasonal_deals', {
    title: 'Christmas-New Year deals!',
    title_highlight: 'Limited-time',
    subtitle: 'Travel beyond boundaries with incredible savings',
  });
  const internationalDestinations = getSection('international_destinations', {
    title: 'Destinations',
    title_highlight: 'International',
  });
  const hiddenGems = getSection('hidden_gems', {
    title: 'The Hidden Gems',
    title_highlight: 'Explore',
  });
  const holidayThemes = getSection('holiday_themes', {
    title: 'Holidays By Theme',
    title_highlight: 'Explore',
  });
  const holidayExploreGems = getSection('holiday_explore_gems', {
    title: 'The Hidden Gems',
    title_highlight: 'Explore',
  });

  const allPlaces = (trendingSection.Items || []).map((item, index) => ({
    id: item.id || index + 1,
    name: item.title || item.name,
    img: item.image_url || item.img,
  }));

  if (loading || !pageContent) {
    return <Preloader />;
  }

  return (
    <div>
      <FrontendPageSEO
        page={pageContent}
        fallbackTitle="Holiday Packages & Tour Deals | Mangoloops"
        fallbackDescription="Explore top trending destinations and limited-time holiday deals with Mangoloops. Book your dream vacation with our curated tour packages."
        fallbackSlug="/holidays"
      />
      <Header onLoginClick={() => setIsLoginPopupOpen(true)} />
      <LoginPopup isOpen={isLoginPopupOpen} onClose={() => setIsLoginPopupOpen(false)} />
      <div className="banner-content tour-search-sec text-center mb-5 mb-lg-8">
        <div className=" container-fluid ">
          <div className=" row justify-content-center py-xl-4">
            <div className=" col-lg-8">
              <div className="card-body">

                {/* SEARCH FORM - same UX as Hotels but searching packages */}
                <form className="form-container search-input-form" onSubmit={handleSearchSubmit}>
                  <div className="search-container">
                    <span><i className="fa fa-search search-icon"></i></span>
                    <input
                      type="text"
                      placeholder="Enter City or Package name"
                      className="search-input"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      autoComplete="off"
                    />
                    <button type="submit" className="search-button">Search</button>

                    {isSuggestionsLoading && (
                      <div className="suggestions-dropdown"><div className="suggestion-item">Loading...</div></div>
                    )}

                    {!isSuggestionsLoading && suggestions.length > 0 && (
                      <div className="suggestions-dropdown">
                        {suggestions.map((item, index) => (
                          <div
                            key={`${item.type}-${item.id || index}`}
                            className="suggestion-item"
                            onMouseDown={() => handleSuggestionClick(item)}
                          >
                            <strong>{item.name}</strong>
                            {item.type === 'package' && item.city && (
                              <small>Package - {item.city}</small>
                            )}
                            {item.type === 'city' && <small>City</small>}
                          </div>
                        ))}
                      </div>
                    )}

                    {!isSuggestionsLoading && suggestions.length === 0 && searchQuery.trim().length >= 2 && (
                        <div className="suggestions-dropdown">
                            <div className="suggestion-item text-muted">
                                No results found for "{searchQuery}"
                            </div>
                        </div>
                    )}
                  </div>
                </form>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/*****Slider Top Tranding Destinations **************/}

      {isSectionActive('trending_destinations') && (
      <div className="container swiper pb-5 holidays-left">
        <div className="popular-state pb-0 pt-0 mb-5 holidays-container-left">
          <div className="container">
            <div className="title">
              <DynamicSectionTitle section={trendingSection} className="pb-0" />
              <p>{trendingSection.subtitle}</p>
            </div>
          </div>
        </div>

        <div className="row holidays-page-space">
          {allPlaces.map(place => (
            <div className="col-lg-2 mobile-view-w-50" key={place.id}>
              <a href="/holidays-list" target="_blank" rel="noreferrer">
                <div className="image-container">
                  <img className="br-15 top-destination-img-h " src={place.img} alt={place.name} width="100%" />
                </div>
                <div className="">
                  <h4 className="country-name-destination">{place.name}</h4>
                </div>
              </a>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Keep the rest of your original content exactly as it was */}
      {isSectionActive('seasonal_deals') && (
      <div className="container swiper pb-5 holidays-left">
        <div className="popular-state pb-0 pt-0 mb-5 holidays-container-left">
          <div className="container">
            <div className="title">
              <DynamicSectionTitle section={seasonalDeals} className="pb-0" />
              <p>{seasonalDeals.subtitle}</p>
            </div>
          </div>
        </div>

        {/* Example top deals - keep your original markup if you have specifics */}
        <div className="row desktop-view">
          <div className="col-lg-4">
            <div className="team-item">
              <div className="team-image">
                <a href="/holidays-list" target="_blank" className="image-anime" rel="noreferrer">
                  <figure><img src="assets/img/tours/new-year-deals/kashmir.jpg" alt="" /></figure>
                </a>
              </div>
              <div className="team-content">
                <h3><a className="text-white" href="#">Kashmir</a></h3>
                <p className="text-white mb-0">5 Nights / 6 Days</p>
              </div>
            </div>
          </div>

          {/* ... keep the rest of your original page content exactly as before ... */}
        </div>
      </div>
      )}

      {isSectionActive('holiday_themes') && <SlidersHolidays section={holidayThemes} />}
      {isSectionActive('international_destinations') && <SwipperHolidays section={internationalDestinations} />}
      {isSectionActive('hidden_gems') && <Sliders1_Holidays section={hiddenGems} />}
      {isSectionActive('holiday_explore_gems') && <SliderHolidaysExplore section={holidayExploreGems} />}

      {/* ── Dynamically render any NEW sections added by super admin ── */}
      {sections
        .filter(s => !KNOWN_SECTION_KEYS.has(s.section_key) && s.status === 'active')
        .map(s => (
          <GenericDynamicSection key={s.id} section={s} />
        ))
      }

      <Footer />
    </div>
  );
}

export default Holidays;
